import Cookies from 'js-cookie'
import { useQuery } from 'react-query'
import { gql, useMutation, ApolloError } from '@apollo/client'

interface TrackViewVars {
  customerIp: string
  userAgent: string
  sku: string
  fbc: string
  fbp: string
}

interface TrackRegistrationVars {
  customerIp: string
  userAgent: string
  email: string
  fbc: string
  fbp: string
}

interface TrackCompletePurchaseVars {
  orderId: string
  customerIp: string
  userAgent: string
  fbc: string
  fbp: string
}

interface TrackInitiatePurchaseVars {
  customerIp: string
  userAgent: string
  fbc: string
  fbp: string
}

interface TrackAddingToCartVars {
  sku: string
  qty: number
  customerIp: string
  userAgent: string
  fbc: string
  fbp: string
}

interface TrackAddingToCartProps {
  sku: string
  qty: number
}

const GET_IP_SERVICE = 'https://geolocation-db.com/json/0f761a30-fe14-11e9-b59f-e53803842572'

export const useFacebookConversions = () => {
  const [trackContentView] = useMutation<Response, TrackViewVars>(fbTrackViewContent, { errorPolicy: 'ignore', onError: (error) => handleError(error) })
  const [trackRegistration] = useMutation<Response, TrackRegistrationVars>(fbTrackCompleteRegistration, { errorPolicy: 'ignore', onError: (error) => handleError(error) })
  const [trackCompletePurchase] = useMutation<Response, TrackCompletePurchaseVars>(fbTrackPurchase, { errorPolicy: 'ignore', onError: (error) => handleError(error) })
  const [trackInitiatePurchase] = useMutation<Response, TrackInitiatePurchaseVars>(fbTrackInitiateCheckout, { errorPolicy: 'ignore', onError: (error) => handleError(error) })
  const [trackAddingToCart] = useMutation<Response, TrackAddingToCartVars>(fbTrackAddToCart, { errorPolicy: 'ignore', onError: (error) => handleError(error) })
  const userAgent = navigator.userAgent ?? ''
  const fbc = Cookies.get('_fbc') ?? ''
  const fbp = Cookies.get('_fbp') ?? ''

  const handleError = (error: ApolloError) => {
    // eslint-disable-next-line
    console.error(error)
  }

  const initTracking = async () =>
    await (await fetch(GET_IP_SERVICE)).json()

  const { data: customerData } = useQuery('customerData', initTracking)

  const customerIp = customerData?.IPv4

  const trackProductView = ({ sku }: { sku: string }) => {
    if (customerIp) {
      trackContentView({
        variables: {
          customerIp: customerIp ?? '',
          userAgent,
          fbp,
          fbc,
          sku
        }
      })
    }
  }

  const trackEmailSignUp = ({ email }: { email: string }) => {
    if (customerIp) {
      trackRegistration({
        variables: {
          customerIp: customerIp ?? '',
          userAgent,
          fbp,
          fbc,
          email
        }
      })
    }
  }

  const trackCheckoutSuccess = ({ orderId }: { orderId: string }) => {
    if (customerIp) {
      trackCompletePurchase({
        variables: {
          customerIp: customerIp ?? '',
          userAgent,
          fbp,
          fbc,
          orderId
        }
      })
    }
  }

  const trackCheckout = () => {
    if (customerIp) {
      trackInitiatePurchase({
        variables: {
          customerIp: customerIp ?? '',
          userAgent,
          fbp,
          fbc
        }
      })
    }
  }

  const trackAddToCart = (props: TrackAddingToCartProps) => {
    if (customerIp) {
      trackAddingToCart({
        variables: {
          customerIp: customerIp ?? '',
          userAgent,
          fbp,
          fbc,
          ...props
        }
      })
    }
  }

  return {
    customerData: customerIp,
    initTracking,
    trackProductView,
    trackEmailSignUp,
    trackAddToCart,
    trackCheckout,
    trackCheckoutSuccess
  }
}

const fbTrackViewContent = gql`
  mutation fbTrackViewContent(
    $customerIp: string
    $userAgent: string
    $sku: string
    $fbc: string
    $fbp: string
  ) {
    fbTrackViewContent(
      input: {
        customer_ip: $customerIp
        user_agent: $userAgent
        sku: $sku
        fbc: $fbc
        fbp: $fbp
      }
    ) {
      success
      fb_trace_id
      message
    }
  }
`

const fbTrackCompleteRegistration = gql`
  mutation fbTrackCompleteRegistration(
    $customerIp: string
    $userAgent: string
    $email: string
    $fbc: string
    $fbp: string
  ) {
    fbTrackCompleteRegistration(
      input: {
        customer_ip: $customerIp
        user_agent: $userAgent
        email: $email
        fbc: $fbc
        fbp: $fbp
      }
    ) {
      success
      message
      fb_trace_id
    }
  }
`

const fbTrackPurchase = gql`
  mutation fbTrackPurchase(
    $orderId: string
    $customerIp: string
    $userAgent: string
    $fbc: string
    $fbp: string
  ) {
    fbTrackPurchase(
      input: {
        order_id: $orderId
        customer_ip: $customerIp
        user_agent: $userAgent
        fbc: $fbc
        fbp: $fbp
      }
    ) {
      success
      message
      fb_trace_id
    }
  }
`

const fbTrackInitiateCheckout = gql`
  mutation fbTrackInitiateCheckout(
    $customerIp: string
    $userAgent: string
    $fbc: string
    $fbp: string
  ){
    fbTrackInitiateCheckout(input: {
      customer_ip: $customerIp
      user_agent: $userAgent
      fbc: $fbc
      fbp: $fbp
    }) {
      success
      fb_trace_id
      message
    }
  }
`

const fbTrackAddToCart = gql`
  mutation fbTrackAddToCart(
    $sku: string
    $qty: number
    $customerIp: string
    $userAgent: string
    $fbc: string
    $fbp: string
  ){
    fbTrackAddToCart(input: {
      sku: $sku
      qty: $qty
      customer_ip: $customerIp
      user_agent: $userAgent
      fbc: $fbc
      fbp: $fbp
    }) {
      success
      fb_trace_id
      message
    }
  }
`
